import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
	Bar,
	CartesianGrid,
	ComposedChart,
	Legend,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis
} from 'recharts';
import { Button } from 'react-bootstrap';
import html2canvas from 'html2canvas';
import Utils from '../util/Utils.jsx';
import { summarizeProjectFilter } from '../util/CalendarLogic.jsx';
import { useStateValue } from '../context/state.jsx';
import {
	composeResourceGraphCanvas,
	downloadCanvasPng,
	resourceGraphExportScale,
	resourceGraphExportTitleLines,
	resourceGraphImageFilename
} from '../util/resourceGraphExport.js';

const TYPE_OPTIONS = [
	{ value: 'labor', label: 'Labor' },
	{ value: 'nonlabor', label: 'Nonlabor' },
	{ value: 'material', label: 'Material' },
	{ value: 'all', label: 'All' }
];

const SCALE_OPTIONS = [
	{ value: 'weekly', label: 'Weekly (week-ending Sunday)' },
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'quarterly', label: 'Quarterly' }
];

const SUGGESTED_HOURS = {
	weekly: 50,
	monthly: 200,
	quarterly: 825
};

const intervalNoun = scale => {
	if (scale === 'monthly') {
		return 'month';
	}
	if (scale === 'quarterly') {
		return 'quarter';
	}
	return 'week';
};

const formatCompact = value => {
	if (value == null || Number.isNaN(Number(value))) {
		return '—';
	}
	const n = Number(value);
	if (n === 0) {
		return '0';
	}
	const abs = Math.abs(n);
	let scaled = n;
	let suffix = '';
	if (abs >= 1e6) {
		scaled = n / 1e6;
		suffix = 'm';
	} else if (abs >= 1000) {
		scaled = n / 1e3;
		suffix = 'k';
	}
	const digits = Math.floor(Math.log10(Math.abs(scaled)));
	const decimals = Math.max(0, 2 - digits);
	return scaled.toFixed(decimals) + suffix;
};

const ResourceGraphView = ({
	filteredActivities,
	datasetInfo,
	projectFilter
}) => {
	const [, dispatch] = useStateValue();
	const [explicitType, setExplicitType] = useState('');
	const [explicitScope, setExplicitScope] = useState('');
	const suppressTypeFetch = useRef('');
	const [scale, setScale] = useState('weekly');
	const [earnFrom, setEarnFrom] = useState('actual');
	const [laborUnit, setLaborUnit] = useState('manhours');
	const [hoursPerPeriod, setHoursPerPeriod] = useState(SUGGESTED_HOURS.weekly);
	const [durationEv, setDurationEv] = useState(false);
	const [durationDeclined, setDurationDeclined] = useState(false);
	const [unitsDraft, setUnitsDraft] = useState('');
	const [committedUnitsPerDay, setCommittedUnitsPerDay] = useState(0);
	const [showBlEarly, setShowBlEarly] = useState(false);
	const [showBlLate, setShowBlLate] = useState(false);
	const [selectedNames, setSelectedNames] = useState({});
	const [payload, setPayload] = useState(null);
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [exportError, setExportError] = useState('');
	const chartBoxRef = useRef(null);
	const [plot, setPlot] = useState({ left: 0, width: 0 });

	const taskIds = useMemo(() => {
		return (filteredActivities || []).map(activity => activity.activityId);
	}, [filteredActivities]);
	const datasetId = datasetInfo && datasetInfo.Id ? String(datasetInfo.Id) : '';
	const datasetName = datasetInfo && datasetInfo.Name ? datasetInfo.Name : '';
	const scopeKey = `${datasetId}|${taskIds.join(',')}`;
	const rsrcType = explicitScope === scopeKey && explicitType ? explicitType : 'auto';

	const selectedNameList = useMemo(() => {
		return Object.keys(selectedNames).filter(name => selectedNames[name]);
	}, [selectedNames]);

	const isLabor = rsrcType === 'labor';
	const useHeadcount = isLabor && laborUnit === 'headcount';

	useEffect(() => {
		if (!datasetId || !taskIds.length) {
			setLoading(false);
			setPayload({
				weeks: [],
				resources: [],
				header: { name: datasetName },
				hasEarly: false,
				hasLate: false,
				hasAssignments: false,
				hasBaseline: false,
				baselineNoMatchingResource: false
			});
			return undefined;
		}
		// The auto response already contains the chosen type's series. Applying
		// that type to the radio must not start a second request.
		if (suppressTypeFetch.current && suppressTypeFetch.current === rsrcType) {
			suppressTypeFetch.current = '';
			return undefined;
		}
		const controller = new AbortController();
		let cancelled = false;
		setLoading(true);
		setError('');
		const requestedType = rsrcType;
		const body = {
			dataset: datasetId,
			taskIds: taskIds.join(','),
			rsrcType: requestedType,
			rsrcNames: selectedNameList.join(','),
			scale,
			earnFrom: earnFrom === 'baseline' ? 'baseline' : 'actual',
			laborUnit: useHeadcount ? 'headcount' : 'manhours',
			hoursPerPeriod,
			durationEv: durationEv ? 1 : 0,
			unitsPerDay: durationEv ? committedUnitsPerDay : 0
		};
		// background: the graph has its own loading state. Tracking this call in
		// the global overlay retriggers the page render that used to refetch it.
		Utils.ajaxAsync(
			'getResourceGraph',
			body,
			dispatch,
			{ suppressErrorAlert: true, background: true, signal: controller.signal }
		).then(result => {
			if (cancelled) {
				return;
			}
			if (!result || result.success === false) {
				setError((result && result.error) || 'Unable to load resource graph.');
				setLoading(false);
				return;
			}
			const chosen = String(result.rsrcType || '');
			if (requestedType === 'auto' && chosen && chosen !== 'auto') {
				suppressTypeFetch.current = chosen;
				setExplicitScope(scopeKey);
				setExplicitType(chosen);
			}
			setPayload(result);
			setLoading(false);
		}).catch(err => {
			if (cancelled || (err && err.name === 'AbortError')) {
				return;
			}
			setError(err && err.message ? err.message : 'Unable to load resource graph.');
			setLoading(false);
		});
		return () => {
			cancelled = true;
			controller.abort();
		};
	}, [
		datasetId,
		datasetName,
		taskIds,
		scopeKey,
		rsrcType,
		selectedNameList,
		scale,
		earnFrom,
		useHeadcount,
		hoursPerPeriod,
		durationEv,
		committedUnitsPerDay,
		dispatch
	]);

	useEffect(() => {
		if (payload && payload.hasBaseline === false && earnFrom === 'baseline') {
			setEarnFrom('actual');
		}
	}, [payload, earnFrom]);

	const weeks = payload && Array.isArray(payload.weeks) ? payload.weeks : [];
	const resources = payload && Array.isArray(payload.resources) ? payload.resources : [];
	const header = (payload && payload.header) || {};
	const filterSummary = summarizeProjectFilter(projectFilter);
	const projectName = header.name || (datasetInfo && datasetInfo.Name) || 'Project';
	const baselineFile = header.baselineFilename || header.baselineLabel || '';
	const baselineInProgress = Boolean(header.baselineInProgress);
	const hasAssignments = Boolean(payload && payload.hasAssignments);
	const hasBaseline = Boolean(payload && payload.hasBaseline);
	const baselineNoMatchingResource = Boolean(payload && payload.baselineNoMatchingResource);
	const showDurationPrompt = Boolean(
		payload
		&& !loading
		&& taskIds.length
		&& !hasAssignments
		&& !durationEv
	);
	const unitLabel = useHeadcount ? 'headcount' : 'MH';
	const defaultUnitsPerDay = (payload && payload.unitsPerDay) || 8;
	const parseUnits = raw => {
		const n = Number(raw);
		return n > 0 ? n : 0;
	};
	const applyDurationEv = () => {
		setCommittedUnitsPerDay(parseUnits(unitsDraft));
		setDurationDeclined(false);
		setDurationEv(true);
	};

	const toggleName = name => {
		setSelectedNames(prev => {
			const next = { ...prev };
			if (next[name]) {
				delete next[name];
			} else {
				next[name] = true;
			}
			return next;
		});
	};

	const changeScale = nextScale => {
		setScale(nextScale);
		setHoursPerPeriod(SUGGESTED_HOURS[nextScale] || 50);
	};

	const chartData = weeks.map(week => ({
		...week,
		name: week.endLabel
	}));

	const showEarly = showBlEarly && payload && payload.hasEarly;
	const showLate = showBlLate && payload && payload.hasLate;
	const intervalLabel = intervalNoun(scale);
	const scaleLabel = (SCALE_OPTIONS.find(option => option.value === scale) || {}).label || scale;
	const rangeStart = chartData.length ? chartData[0].name : '';
	const rangeEnd = chartData.length ? chartData[chartData.length - 1].name : '';

	const exportImage = () => {
		const node = chartBoxRef.current;
		if (!node || exporting) {
			return;
		}
		setExporting(true);
		setExportError('');
		const scaleFactor = resourceGraphExportScale(window.devicePixelRatio);
		const nameNote = selectedNameList.length ? `${selectedNameList.length} resource names` : '';
		const earnNote = earnFrom === 'baseline' ? 'earn from baseline' : 'earn from actual units';
		html2canvas(node, {
			scale: scaleFactor,
			backgroundColor: '#ffffff',
			logging: false,
			useCORS: true,
			onclone: doc => {
				const chart = doc.querySelector('[data-resource-graph-chart]');
				if (chart) {
					chart.style.background = '#ffffff';
				}
			}
		}).then(chartCanvas => {
			const lines = resourceGraphExportTitleLines({
				projectName,
				datasetId: datasetInfo && datasetInfo.Id,
				filterSummary,
				scaleLabel,
				rsrcType,
				unitLabel,
				rangeStart,
				rangeEnd,
				earnNote,
				nameNote
			});
			const canvas = composeResourceGraphCanvas(chartCanvas, lines);
			const filename = resourceGraphImageFilename({
				project: projectName,
				dataset: datasetInfo && datasetInfo.Id
			});
			return downloadCanvasPng(canvas, filename);
		}).catch(() => {
			setExportError('Unable to export the resource graph image.');
		}).finally(() => {
			setExporting(false);
		});
	};

	useLayoutEffect(() => {
		const root = chartBoxRef.current;
		if (!root) {
			return undefined;
		}
		const measure = () => {
			const grid = root.querySelector('.recharts-cartesian-grid');
			if (!grid) {
				return;
			}
			const rootBox = root.getBoundingClientRect();
			const gridBox = grid.getBoundingClientRect();
			const nextLeft = Math.max(0, gridBox.left - rootBox.left);
			const nextWidth = Math.max(0, gridBox.width);
			setPlot(prev => {
				if (Math.abs(prev.left - nextLeft) < 0.5 && Math.abs(prev.width - nextWidth) < 0.5) {
					return prev;
				}
				return { left: nextLeft, width: nextWidth };
			});
		};
		measure();
		const frame = window.requestAnimationFrame(measure);
		const observer = new ResizeObserver(measure);
		observer.observe(root);
		return () => {
			window.cancelAnimationFrame(frame);
			observer.disconnect();
		};
	}, [chartData.length, showEarly, showLate, loading, scale, rsrcType]);

	const alignedStubStyle = plot.width ? { width: plot.left, flex: '0 0 auto' } : undefined;
	const alignedBandStyle = weeks.length
		? {
			gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))`,
			...(plot.width ? { width: plot.width, flex: '0 0 auto' } : {})
		}
		: undefined;

	const intervalTableRows = [
		['Earned', 'earnedWeekly'],
		['Forecast', 'forecastWeekly'],
		hasBaseline ? ['Baseline', 'bl5050Weekly'] : null
	].filter(Boolean);
	const cumulativeTableRows = [
		['Earned', 'earnedCum'],
		['Forecast', 'forecastCum'],
		hasBaseline ? ['Baseline', 'bl5050Cum'] : null,
		hasBaseline && showEarly ? ['BL Early', 'blEarlyCum'] : null,
		hasBaseline && showLate ? ['BL Late', 'blLateCum'] : null
	].filter(Boolean);

	const tableWeeks = useMemo(() => {
		let earnedHold = null;
		return weeks.map(week => {
			if (week.earnedCum != null) {
				earnedHold = week.earnedCum;
			}
			return {
				...week,
				earnedCum: week.earnedCum == null ? earnedHold : week.earnedCum
			};
		});
	}, [weeks]);

	const renderAlignedRow = (label, key, values) => (
		<div className="resourceGraphAlignedRow" key={key}>
			<div className="resourceGraphAlignedStub" style={alignedStubStyle} title={label}>{label}</div>
			<div className="resourceGraphAlignedBands" style={alignedBandStyle}>
				{values.map(week => (
					<div key={`${key}-${week.endTs}`} className="resourceGraphAlignedCell">
						{formatCompact(week[key])}
					</div>
				))}
			</div>
		</div>
	);

	const renderSectionHead = label => (
		<div className="resourceGraphAlignedRow resourceGraphAlignedSection" key={`section-${label}`}>
			<div className="resourceGraphAlignedStub" style={alignedStubStyle}>{label}</div>
			<div className="resourceGraphAlignedBands" style={alignedBandStyle} />
		</div>
	);

	return (
		<div className="resourceGraphView">
			<div className="resourceGraphToolbar no-print">
				<div className="resourceGraphToolbarRow">
					<div className="resourceGraphTypes">
						{TYPE_OPTIONS.map(option => (
							<label key={option.value}>
								<input
									type="radio"
									name="resourceGraphType"
									checked={rsrcType === option.value}
									onChange={() => {
										suppressTypeFetch.current = '';
										setExplicitScope(scopeKey);
										setExplicitType(option.value);
									}} />
								{option.label}
							</label>
						))}
					</div>
					<div className="resourceGraphTypes">
						{SCALE_OPTIONS.map(option => (
							<label key={option.value}>
								<input
									type="radio"
									name="resourceGraphScale"
									checked={scale === option.value}
									onChange={() => { changeScale(option.value); }} />
								{option.label}
							</label>
						))}
					</div>
					<div className="resourceGraphToolbarActions">
						<Button
							className="resourceGraphExportBtn"
							onClick={exportImage}
							disabled={exporting || loading}
							aria-busy={exporting}>
							{exporting ? 'Exporting…' : 'Export image'}
						</Button>
						<Button className="resourceGraphPrintBtn" onClick={() => { window.print(); }}>Print</Button>
					</div>
				</div>
				{baselineFile || hasBaseline || baselineNoMatchingResource
					? (
						<div className="resourceGraphToolbarRow">
							<div className="resourceGraphBaselineFile">
								<strong>Baseline file:</strong>
								{' '}
								{baselineFile || 'assigned'}
								{baselineInProgress ? ' (in progress)' : ''}
							</div>
						</div>
					)
					: null}
				{baselineNoMatchingResource
					? (
						<div className="resourceGraphBanner no-print">
							<div>
								Baseline has no matching resource.
								Baseline values are not displayed.
							</div>
						</div>
					)
					: null}
				{isLabor
					? (
						<div className="resourceGraphToolbarRow">
							<div className="resourceGraphTypes">
								<label>
									<input
										type="radio"
										name="resourceGraphLaborUnit"
										checked={laborUnit === 'manhours'}
										onChange={() => { setLaborUnit('manhours'); }} />
									Manhours
								</label>
								<label>
									<input
										type="radio"
										name="resourceGraphLaborUnit"
										checked={laborUnit === 'headcount'}
										onChange={() => { setLaborUnit('headcount'); }} />
									Headcount
								</label>
							</div>
							{useHeadcount
								? (
									<label className="resourceGraphField">
										Hours / {intervalLabel}
										<input
											type="number"
											min="1"
											step="1"
											value={hoursPerPeriod}
											onChange={event => {
												const next = Number(event.target.value);
												setHoursPerPeriod(next > 0 ? next : SUGGESTED_HOURS[scale]);
											}} />
										<span className="resourceGraphHint">
											Suggested {SUGGESTED_HOURS[scale]}
											{hoursPerPeriod !== SUGGESTED_HOURS[scale]
												? (
													<button
														type="button"
														className="resourceGraphReset"
														onClick={() => { setHoursPerPeriod(SUGGESTED_HOURS[scale]); }}>
														Reset
													</button>
												)
												: null}
										</span>
									</label>
								)
								: null}
						</div>
					)
					: null}
				{hasBaseline
					? (
						<div className="resourceGraphToolbarRow">
							<div className="resourceGraphTypes">
								<label>
									<input
										type="radio"
										name="resourceGraphEarnFrom"
										checked={earnFrom === 'actual'}
										onChange={() => { setEarnFrom('actual'); }} />
									Earn from actual units
								</label>
								<label>
									<input
										type="radio"
										name="resourceGraphEarnFrom"
										checked={earnFrom === 'baseline'}
										onChange={() => { setEarnFrom('baseline'); }} />
									Earn value from baseline
								</label>
							</div>
							{payload && payload.hasEarly
								? (
									<label>
										<input
											type="checkbox"
											checked={showBlEarly}
											onChange={() => { setShowBlEarly(prev => !prev); }} />
										Show BL Early line
									</label>
								)
								: null}
							{payload && payload.hasLate
								? (
									<label>
										<input
											type="checkbox"
											checked={showBlLate}
											onChange={() => { setShowBlLate(prev => !prev); }} />
										Show BL Late line
									</label>
								)
								: null}
						</div>
					)
					: null}
			</div>

			<div className="resourceGraphNameFilter no-print">
				<div className="resourceGraphNameFilterTitle">Resource name</div>
				<div className="resourceGraphNameList">
					{resources.map(resource => (
						<label key={resource.name}>
							<input
								type="checkbox"
								checked={Boolean(selectedNames[resource.name])}
								onChange={() => { toggleName(resource.name); }} />
							{resource.name}
						</label>
					))}
				</div>
			</div>

			{showDurationPrompt
				? (
					<div className="resourceGraphBanner no-print">
						<div>
							No resources are assigned to the filtered activities.
							Derive earned value from duration using a unit-per-day value?
						</div>
						{!durationDeclined
							? (
								<label className="resourceGraphField">
									Units / day
									<input
										type="number"
										min="0.01"
										step="0.25"
										value={unitsDraft}
										placeholder={String(defaultUnitsPerDay)}
										onChange={event => { setUnitsDraft(event.target.value); }} />
								</label>
							)
							: null}
						<div className="resourceGraphBannerActions">
							<Button
								bsStyle="primary"
								onClick={applyDurationEv}>
								Use duration EV
							</Button>
							{durationDeclined
								? null
								: (
									<Button
										onClick={() => { setDurationDeclined(true); }}>
										No
									</Button>
								)}
						</div>
					</div>
				)
				: null}

			{durationEv && !hasAssignments
				? (
					<div className="resourceGraphToolbarRow no-print">
						<label className="resourceGraphField">
							Duration units / day
							<input
								type="number"
								min="0.01"
								step="0.25"
								value={unitsDraft}
								placeholder={String(defaultUnitsPerDay)}
								onChange={event => { setUnitsDraft(event.target.value); }}
								onBlur={() => { setCommittedUnitsPerDay(parseUnits(unitsDraft)); }} />
						</label>
						<Button
							onClick={() => {
								setDurationEv(false);
								setDurationDeclined(false);
							}}>
							Stop duration EV
						</Button>
					</div>
				)
				: null}

			<div className="resourceGraphPrintHeader">
				<div><strong>JOB:</strong> {projectName}</div>
				<div><strong>STATUS DATE:</strong> {header.dataDate || '—'}</div>
				<div><strong>BASELINE FILE:</strong> {baselineFile || (hasBaseline ? 'assigned' : '—')}</div>
				<div><strong>PRINTED:</strong> {header.printedAt || ''}</div>
				<div><strong>FILTERS:</strong> {filterSummary}</div>
				<div>
					<strong>RESOURCE TYPE:</strong> {rsrcType}
					{selectedNameList.length ? ` (${selectedNameList.length} names)` : ''}
					{' · '}
					{scale}
					{isLabor ? ` · ${useHeadcount ? 'headcount' : 'manhours'}` : ''}
					{earnFrom === 'baseline' ? ' · earn from baseline' : ' · earn from actual units'}
					{durationEv && !hasAssignments ? ` · duration EV @ ${committedUnitsPerDay || defaultUnitsPerDay} / day` : ''}
				</div>
			</div>

			<div className="resourceGraphMilestone">
				<div className="resourceGraphAlignedRow">
					<div className="resourceGraphAlignedStub" style={alignedStubStyle}>Earned %</div>
					<div
						className="resourceGraphAlignedBands"
						style={alignedBandStyle}>
						{weeks.map(week => (
							<div key={`e-${week.endTs}`} className="resourceGraphAlignedCell">
								{week.earnedPct == null ? '—' : `${week.earnedPct}%`}
							</div>
						))}
					</div>
				</div>
				{hasBaseline
					? (
						<div className="resourceGraphAlignedRow">
							<div className="resourceGraphAlignedStub" style={alignedStubStyle}>Planned %</div>
							<div
								className="resourceGraphAlignedBands"
								style={alignedBandStyle}>
								{weeks.map(week => (
									<div key={`p-${week.endTs}`} className="resourceGraphAlignedCell">{week.plannedPct}%</div>
								))}
							</div>
						</div>
					)
					: null}
				{hasBaseline
					? (
						<div className="resourceGraphAlignedRow">
							<div className="resourceGraphAlignedStub" style={alignedStubStyle}>SPI</div>
							<div
								className="resourceGraphAlignedBands"
								style={alignedBandStyle}>
								{weeks.map(week => (
									<div key={`s-${week.endTs}`} className="resourceGraphAlignedCell">
										{week.spi == null ? '—' : formatCompact(week.spi)}
									</div>
								))}
							</div>
						</div>
					)
					: null}
			</div>

			{error ? <div className="resourceGraphError">{error}</div> : null}
			{exportError ? <div className="resourceGraphError">{exportError}</div> : null}
			{loading ? <div className="resourceGraphLoading">Loading graph…</div> : null}

			<div className="resourceGraphChart" ref={chartBoxRef} data-resource-graph-chart>
				{chartData.length
					? (
						<ResponsiveContainer width="100%" height={360}>
							{/* Weekly axis stays Monday–Sunday (week-ending Sunday). Export copies these ticks. */}
							<ComposedChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={60} />
								<YAxis
									yAxisId="interval"
									tick={{ fontSize: 10 }}
									tickFormatter={formatCompact}
									label={{ value: `Interval (${unitLabel})`, angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
								<YAxis
									yAxisId="cum"
									orientation="right"
									tick={{ fontSize: 10 }}
									tickFormatter={formatCompact}
									label={{ value: `Cumulative (${unitLabel})`, angle: 90, position: 'insideRight', style: { fontSize: 10 } }} />
								<Tooltip formatter={formatCompact} />
								<Legend wrapperStyle={{ fontSize: 11 }} />
								<Bar yAxisId="interval" dataKey="earnedWeekly" name={`Earned / ${intervalLabel}`} fill="#337ab7" />
								<Bar yAxisId="interval" dataKey="forecastWeekly" name={`Forecast / ${intervalLabel}`} fill="#f0ad4e" />
								{hasBaseline
									? <Bar yAxisId="interval" dataKey="bl5050Weekly" name={`BL 50/50 / ${intervalLabel}`} fill="#999" />
									: null}
								<Line yAxisId="cum" type="monotone" dataKey="earnedCum" name="Earned cumulative" stroke="#337ab7" dot={false} strokeWidth={2} connectNulls={false} />
								<Line yAxisId="cum" type="monotone" dataKey="forecastCum" name="Forecast cumulative" stroke="#f0ad4e" dot={false} strokeWidth={2} connectNulls={false} />
								{hasBaseline
									? <Line yAxisId="cum" type="monotone" dataKey="bl5050Cum" name="BL 50/50 cumulative" stroke="#555" dot={false} strokeWidth={2} />
									: null}
								{showEarly
									? <Line yAxisId="cum" type="monotone" dataKey="blEarlyCum" name="BL Early cumulative" stroke="#5cb85c" dot={false} strokeDasharray="4 2" />
									: null}
								{showLate
									? <Line yAxisId="cum" type="monotone" dataKey="blLateCum" name="BL Late cumulative" stroke="#d9534f" dot={false} strokeDasharray="4 2" />
									: null}
							</ComposedChart>
						</ResponsiveContainer>
					)
					: (
						<div className="resourceGraphEmpty">
							{showDurationPrompt && durationDeclined
								? 'Duration-based earned value was not applied.'
								: 'No resource hours for the current filters.'}
						</div>
					)}
			</div>

			<div className="resourceGraphTableWrap">
				<div className="resourceGraphAlignedRow resourceGraphAlignedHead">
					<div className="resourceGraphAlignedStub" style={alignedStubStyle}>Series</div>
					<div className="resourceGraphAlignedBands" style={alignedBandStyle}>
						{tableWeeks.map(week => (
							<div key={`h-${week.endTs}`} className="resourceGraphAlignedCell">{week.endLabel}</div>
						))}
					</div>
				</div>
				{renderSectionHead('Interval')}
				{intervalTableRows.map(row => renderAlignedRow(row[0], row[1], tableWeeks))}
				{renderSectionHead('Cumulative')}
				{cumulativeTableRows.map(row => renderAlignedRow(row[0], row[1], tableWeeks))}
			</div>
		</div>
	);
};

ResourceGraphView.propTypes = {
	filteredActivities: PropTypes.array,
	datasetInfo: PropTypes.object,
	projectFilter: PropTypes.object
};

export default ResourceGraphView;
